/**
 * Modo simulación: la aplicación real contra datos de ejemplo.
 *
 * Existe para que alguien pueda recorrer Trellis desde la página pública sin
 * credenciales y sin tocar datos de ninguna institución.
 *
 * Dos decisiones sostienen que esto no se convierta en una segunda
 * implementación que se desactualiza:
 *
 * 1. **Los datos no se escriben a mano.** `scripts/build-demo.mjs` los arma con
 *    las mismas plantillas clínicas que usa la API, y se regenera con un
 *    comando; nadie mantiene un JSON a mano.
 * 2. **Se intercepta en un solo lugar**, el cliente de API. Ninguna pantalla ni
 *    componente tiene una rama "si es demo": para ellos no existe este archivo.
 *
 * Lo que no puede simular: todo lo que calcula el servidor. Cargar un dolor de 9
 * no dispara una alerta, porque esa evaluación vive en la API. Es una recorrida
 * de la interfaz, no un entorno de pruebas.
 */

/** Respuestas capturadas de la API real, indexadas por ruta. */
type Snapshot = Record<string, unknown>;

let snapshot: Snapshot | null = null;

/** Copia de trabajo: las escrituras de la sesión se aplican acá y nada más. */
let working: Snapshot = {};

const FLAG = 'trellis:demo';

export function isDemoMode(): boolean {
  try {
    return sessionStorage.getItem(FLAG) === 'on';
  } catch {
    return false;
  }
}

/**
 * Enciende la simulación.
 *
 * Va en `sessionStorage` y no en la URL para que sobreviva a la navegación
 * interna sin ensuciar cada enlace, y muera al cerrar la pestaña: cada visitante
 * arranca con los datos intactos.
 */
export function startDemo(): void {
  try {
    sessionStorage.setItem(FLAG, 'on');
  } catch {
    // Modo privado con almacenamiento bloqueado: la simulación no arranca, y la
    // aplicación sigue pidiéndole credenciales como a cualquiera.
  }
}

export function stopDemo(): void {
  try {
    sessionStorage.removeItem(FLAG);
  } catch {
    // Nada que hacer.
  }
  working = {};
}

/** Carga los datos capturados. Se pide una sola vez por sesión. */
export async function loadSnapshot(): Promise<void> {
  if (snapshot) return;

  const response = await fetch('/demo-snapshot.json');
  if (!response.ok) {
    // Pasa cuando nadie corrió `npm run demo:build` todavía. Se dice qué falta
    // en vez de dejar la pantalla en blanco.
    throw new Error(
      'La simulación todavía no tiene datos cargados. Generalos con "npm run demo:build".',
    );
  }

  snapshot = (await response.json()) as Snapshot;
  working = { ...snapshot };
}

/** Normaliza la ruta para buscarla: sin barra final y con las claves ordenadas. */
function keyOf(path: string): string {
  const [route, query] = path.split('?');
  const clean = route!.replace(/\/$/, '');

  if (!query) return clean;

  const params = new URLSearchParams(query);
  const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
  return sorted.length > 0 ? `${clean}?${new URLSearchParams(sorted).toString()}` : clean;
}

/** La misma ruta sin la cadena de consulta. */
function pathOf(path: string): string {
  return path.split('?')[0]!.replace(/\/$/, '');
}

/**
 * Aplica los filtros de la pantalla sobre una lista ya resuelta.
 *
 * Es lo que hace que los filtros de la consola no sean decorativos: sin esto,
 * elegir "Resueltas" en la cola de alertas devolvía igual la lista completa, con
 * pendientes adentro, y la simulación parecía rota en la primera interacción.
 *
 * Solo cubre los filtros que existen hoy en la consola —estado, severidad,
 * publicado y búsqueda por texto—; cualquier otro simplemente no recorta.
 */
function filterList(route: string, data: unknown, query: URLSearchParams): unknown {
  const list = data as { items?: unknown } | null;
  if (!list || typeof list !== 'object' || !Array.isArray(list.items)) return data;

  let items = list.items as Array<Record<string, unknown>>;

  const status = query.get('status');
  if (status) {
    items = items.filter((item) => item.status === status);
  } else if (route === '/console/alerts') {
    // Sin filtro elegido, la cola muestra lo pendiente. Es el criterio de la
    // API, y acá hay que repetirlo porque no hay servidor que lo aplique.
    items = items.filter((item) => item.status === 'OPEN' || item.status === 'ACKNOWLEDGED');
  }

  const severity = query.get('severity');
  if (severity) items = items.filter((item) => item.severity === severity);

  const published = query.get('published');
  if (published) items = items.filter((item) => item.published === (published === 'true'));

  const serious = query.get('serious');
  if (serious) items = items.filter((item) => item.isSerious === (serious === 'true'));

  const search = query.get('search')?.trim().toLowerCase();
  if (search) {
    items = items.filter((item) => JSON.stringify(item).toLowerCase().includes(search));
  }

  return { ...list, items, total: items.length };
}

/**
 * Resuelve un pedido contra los datos de ejemplo.
 *
 * Devuelve `undefined` cuando no hay nada capturado para esa ruta, y quien llama
 * decide qué hacer. No se inventa una respuesta vacía: una pantalla en blanco
 * sin explicación es peor que un error.
 */
export function resolveDemo<T>(path: string, method: string): T | undefined {
  if (method === 'GET') {
    /**
     * Primero la ruta exacta y después la ruta sin la cadena de consulta.
     *
     * Las pantallas arman la consulta con lo que el visitante elige —el filtro
     * de estado de las alertas, la búsqueda de pacientes, el tamaño de página—
     * y enumerar cada combinación sería imposible. Con esta caída, todas
     * resuelven contra el mismo conjunto, y después se filtra acá.
     */
    const found = working[keyOf(path)] ?? working[pathOf(path)];
    if (found === undefined) return undefined;

    return filterList(pathOf(path), found, new URLSearchParams(path.split('?')[1] ?? '')) as T;
  }

  /**
   * Las escrituras no persisten, pero tampoco fallan.
   *
   * Devolver un error rompería la recorrida en la primera acción; guardar de
   * verdad obligaría a un backend. Se responde lo que la pantalla espera —el
   * cuerpo enviado, con un identificador— y al recargar todo vuelve al estado
   * inicial.
   */
  return { id: `demo-${Math.random().toString(36).slice(2, 10)}`, ok: true } as T;
}

/**
 * La sesión de la simulación, armada acá y no pedida a la API.
 *
 * Tiene que existir de verdad en el proveedor de autenticación: la consola
 * redirige al ingreso cuando no hay usuario, y sin esto la recorrida rebotaba
 * entre la puerta de la simulación y el login sin llegar a ninguna pantalla.
 */
export function demoSession(): { accessToken: string; user: DemoUser } {
  return {
    accessToken: 'demo',
    user: {
      id: 'demo-user',
      role: 'PHARMA_ADMIN',
      fullName: 'Equipo de demostración',
      username: 'equipo@vivirmejor.demo',
      email: 'equipo@vivirmejor.demo',
      tenantSlug: 'simulacion',
      patientId: null,
      mustChangePassword: false,
    },
  };
}

/** El mismo usuario de sesión que maneja el proveedor de autenticación. */
type DemoUser = import('./auth').SessionUser;
